import './styles.css';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { api, ApiError, type User } from './api/client';
import { handleCallback, isLoggedIn, onAuthChange } from './auth/oidc';
import { loadVision } from './media/pipeline';
import { normalizeRoomCode } from './net/room';
import { h, toast } from './ui/dom';
import { renderBooth } from './ui/booth';
import { renderHome } from './ui/home';
import { addRoutes, navigate, render, setGuard, startRouter, type Route } from './ui/router';
import { spinner } from './ui/components';
import { startLive, stopLive } from './ui/live';
import { initPush } from './ui/push';
import { renderLogin } from './ui/screens/login';
import { renderPrivacy } from './ui/screens/privacy';
import { askConsent, hasConsent } from './ui/consent';
import { renderSettings } from './ui/screens/settings';
import { renderFeed } from './ui/screens/feed';
import { renderPost } from './ui/screens/post';
import { renderFriends } from './ui/screens/friends';
import { renderProfile } from './ui/screens/profile';
import { renderRooms } from './ui/screens/rooms';
import { renderRoom } from './ui/screens/room';
import { renderLibrary } from './ui/screens/library';
import { renderNotifications } from './ui/screens/notifications';
import { renderMe } from './ui/screens/me';
import { renderMemories } from './ui/screens/memories';

const root = document.getElementById('app')!;

// ------------------------------------------------------------------ current user
let me: User | null = null;
async function currentUser(): Promise<User> {
  if (me) return me;
  me = await api.me();
  startLive();
  void initPush();
  return me;
}
onAuthChange((on) => {
  if (on) return;
  me = null;
  stopLive();
  if (!/^#\/(login|settings|quick|privacy)/.test(location.hash)) navigate('#/login', true);
});

/** Route needing a user: show a spinner while loading the profile. */
const withMe = (fn: (root: HTMLElement, me: User, params: Record<string, string>) => unknown): Route['render'] =>
  async (el, params) => {
    if (!me) el.replaceChildren(h('main.loading', {}, spinner()));
    try {
      const u = await currentUser();
      return (await fn(el, u, params)) as ReturnType<Route['render']>;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return navigate('#/login', true);
      el.replaceChildren(h('main.loading', {}, h('p', {}, (e as Error).message),
        h('button.secondary', { onclick: () => void render() }, 'Thử lại')));
    }
  };

// ------------------------------------------------------------------ routes
addRoutes([
  { path: '/login', render: (el) => renderLogin(el) },
  { path: '/privacy', render: (el) => renderPrivacy(el) },
  { path: '/settings', render: (el) => renderSettings(el) },
  {
    path: '/quick',
    render: (el) => {
      let cleanup: (() => void) | undefined;
      renderHome(el, (req) => {
        void renderBooth(el, req, () => navigate('#/quick')).then((c) => (cleanup = c));
      });
      return () => cleanup?.();
    },
  },
  { path: '/feed', auth: true, render: withMe((el, u) => renderFeed(el, u)) },
  { path: '/memories', auth: true, render: withMe((el, u) => renderMemories(el, u)) },
  { path: '/post/:id', auth: true, render: withMe((el, u, p) => renderPost(el, u, p.id)) },
  { path: '/friends', auth: true, render: withMe((el, u) => renderFriends(el, u)) },
  { path: '/u/:id', auth: true, render: withMe((el, u, p) => renderProfile(el, u, p.id)) },
  { path: '/rooms', auth: true, render: withMe((el, u) => renderRooms(el, u)) },
  { path: '/room/:id', auth: true, render: withMe((el, u, p) => renderRoom(el, u, p.id)) },
  {
    path: '/booth/:id', auth: true, render: withMe(async (el, u, p) => {
      const room = await api.room(p.id);
      const mode = (localStorage.getItem('booth.mode') as 'ai' | 'greenscreen' | null) ?? 'ai';
      return renderBooth(el, {
        code: room.code,
        role: room.owner.id === u.id ? 'host' : 'guest',
        name: u.displayName,
        mode,
        room,
      }, () => navigate(`#/room/${room.id}`));
    }),
  },
  { path: '/join/:code', auth: true, render: withMe(async (_el, _u, p) => {
    const r = await api.joinRoom(normalizeRoomCode(p.code)).catch((e) => { toast((e as Error).message, 4000); return null; });
    navigate(r ? `#/booth/${r.id}` : '#/rooms', true);
  }) },
  { path: '/library', auth: true, render: withMe((el, u) => renderLibrary(el, u)) },
  { path: '/notifications', auth: true, render: withMe((el, u) => renderNotifications(el, u)) },
  { path: '/me', auth: true, render: withMe((el, u) => renderMe(el, u, (nu) => (me = nu))) },
  { path: '*', render: () => navigate(isLoggedIn() ? '#/feed' : '#/login', true) },
]);

setGuard((r) => {
  if (r.auth && !isLoggedIn()) return '#/login';
  if ((r.path === '/login') && isLoggedIn()) return '#/feed';
  return null;
});

// ------------------------------------------------------------------ start-up
async function boot() {
  // Luật BVDLCN: explicit consent before any camera/account processing (once per policy version)
  if (!hasConsent()) await askConsent(root);
  // web: returning from Keycloak with ?code=…&state=…
  try {
    const back = await handleCallback(location.href);
    if (back) history.replaceState(null, '', location.pathname + back);
  } catch (e) {
    toast((e as Error).message, 5000);
  }
  // invite links: ?room=CODE → join screen (quick mode when logged out)
  const code = new URLSearchParams(location.search).get('room');
  if (code && !location.hash.startsWith('#/join')) {
    history.replaceState(null, '', location.pathname + (isLoggedIn() ? `#/join/${normalizeRoomCode(code)}` : `#/quick?room=${normalizeRoomCode(code)}`));
  }
  await startRouter(root);
}

// Android deep links: login callback (boothtogether://auth/callback) and invites (boothtogether://join?room=X)
if (Capacitor.isNativePlatform()) {
  void App.addListener('appUrlOpen', async ({ url }) => {
    try {
      if (url.startsWith('boothtogether://auth')) {
        const back = await handleCallback(url.replace(/^boothtogether:\/\//, 'https://app/'));
        if (back) navigate(back, true);
        return;
      }
      const room = new URL(url.replace(/^boothtogether:\/\//, 'https://app/')).searchParams.get('room');
      if (room) navigate(isLoggedIn() ? `#/join/${normalizeRoomCode(room)}` : `#/quick?room=${normalizeRoomCode(room)}`);
    } catch (e) {
      toast((e as Error).message, 5000);
    }
  });
}

void boot();
// warm up the wasm + models in the background
setTimeout(() => void loadVision().catch(() => {}), 1500);
